import React, { useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import SubscriptionSuggestionCard from './SubscriptionSuggestionCard'
import PortaledModal from '../../../../components/Modals/PortaledModal'
import MyAccountSubscriptionCategoryModal from '../../../../components/Modals/MyAccountSubscriptionCategoryModal'
import { MainTracker } from '../../../../PixelTrackers/MainTracker'
import * as translator from '../../../../utils/translator'
import InnerHTMLRenderer from '../../../../components/InnerHTMLRenderer/InnerHTMLRenderer'


type props = {
  rootCodeCategory: string
  rootTitle: string
  categories: {
    root_title: string
    id: number
    title: string
    mention: string
    shortcut: string
    link: string
  }[]
  image: string
}

const SubscriptionRootCategoryCard: React.FC<props> = ({
  rootCodeCategory,
  rootTitle,
  categories,
  image
}) => {

  const [showModal, setShowModal] = useState(false);
  const {language, country} = useOutletContext<any>(); // The My Account layout context
  const categoryTheme = rootCodeCategory.includes('women') ? undefined : rootCodeCategory.includes('men') ? 'mens' : 'general';

  // Health categories render as a single German compound word with no space (e.g. "Männergesundheit"),
  // while others are genuinely multi-word ("Allgemeine Medizin"). Split on the first space when there
  // is one; otherwise fall back to splitting off a trailing "gesundheit" (health) suffix, the only
  // known compound boundary in this data. If neither is found, leave the title untouched.
  const [firstTitleWord, ...restTitleWords] = rootTitle.split(' ');
  const restTitleText = restTitleWords.join(' ');
  const compoundSuffixMatch = !restTitleText && rootTitle.match(/gesundheit$/i);

  let title: React.ReactNode = rootTitle;
  if (categoryTheme && restTitleText) {
    // The title container is a flex row (for baseline alignment with the highlight span), and flexbox
    // treats a standalone leading space in a text-node flex item as collapsible line-start whitespace,
    // silently stripping it (e.g. "Allgemeine"+"Medizin" -> "AllgemeineMedizin"). A non-breaking space
    // is never collapsed, so it survives as a real gap between the two flex items.
    title = <><span className="subscription-suggestion-card__title-highlight">{firstTitleWord}</span>{` ${restTitleText}`}</>;
  } else if (categoryTheme && compoundSuffixMatch) {
    const suffix = compoundSuffixMatch[0];
    const prefix = rootTitle.slice(0, rootTitle.length - suffix.length);
    if (prefix) {
      title = <><span className="subscription-suggestion-card__title-highlight">{prefix}</span>{suffix}</>;
    }
  }

  const handleShowModal = () => {
    MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': 'Show categories in subscription tab', 'Root Category': rootTitle})
    setShowModal(true);
  }

  const handleCloseModal = () => {
    MainTracker.track('custom', 'Decision on subscription promo my account', {'Decision': 'Close'})
    setShowModal(false);
  }

  const benefits = [1, 2, 3, 4, 5].map((itemNumber) =>
    itemNumber === 2
      ? <InnerHTMLRenderer key={itemNumber} htmlContent={translator.translate(language, "my_account", "myAccountSubscription/subscription_benefit-item-2")} />
      : translator.translate(language, "my_account", `myAccountSubscription/subscription_benefit-item-${itemNumber}`)
  );

  return (
    <>
      <PortaledModal
        shouldBackgroundClickHideModal={false}
        shouldShowModal={showModal}
        backgroundColor='#0D2C545C'
        hideModal={() => setShowModal(false)}
        showModal={() => setShowModal(true)}
      >
        <MyAccountSubscriptionCategoryModal
          rootTitle={rootTitle}
          categories={categories}
          onClose={handleCloseModal}
          country={country}
          language={language}
        />
      </PortaledModal>

      <SubscriptionSuggestionCard
        theme={categoryTheme}
        image={image}
        title={title}
        includesTitle={translator.translate(language, "my_account", "CarePlus/landing/planIncludes")}
        benefits={benefits}
        categoriesSection={
          <div className="subscription-suggestion-card__categories-section">
            <h3 className="subscription-suggestion-card__section-heading">{translator.translate(language, "my_account", "myAccountSubscription/categories_heading")}</h3>
            <ul className="subscription-suggestion-card__categories-list">
              {categories.map(category => (
                <li key={category.id}>
                  <Link
                    to={`/start-consultation/${country}/${language}/${category.mention}`}
                    onClick={() => MainTracker.track('custom', 'Start consultation from subscription my account', {'Chosen category': category.mention})}
                    className="subscription-suggestion-card__categories-link"
                  >
                    {category.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        }
        paymentHeading={translator.translate(language, "my_account", "myAccountSubscription/payment_heading")}
        showKlarna={country === 'de'}
        cta={{
          label: translator.translate(language, "my_account", "myAccountSubscription/show_categories_button"),
          onClick: handleShowModal,
        }}
      />
    </>
  )
}


export default SubscriptionRootCategoryCard
